import React, { useContext, useState, useEffect, useRef } from "react";
import PropTypes, { element } from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import VariantBuilderPanelCollapse from "../variants/VariantBuilderPanelCollapse";
import VariantBuilderPanelBreadcrumb from "../variants/VariantBuilderPanelBreadcrumb";
import TabGroup from "../shared/TabGroup";
import VariantBuilderPanelTitle from "../variants/VariantBuilderPanelTitle";
import useSections from "../sections/useSections.js";
import useSectionColumns from "../section_columns/useSectionColumns";
import SectionColumnSettings from "../section_columns/SectionColumnSettings";
import NewSectionColumn from "../section_columns/NewSectionColumn";
import SectionLayoutSettings from "./SectionLayoutSettings";
import {
  getLiveFlexSectionColumns,
  isSectionColumnsMobileSorted,
  flexWidthResetUpdates,
  flexColumnLayoutCanReset,
  flexColumnLayoutResetUpdates,
} from "../../utils/builderUtils";
import { getPanelParentBackLink } from "../../utils/panelNavigationUtils";
import useCodeIndicators from "../../hooks/useCodeIndicators";

const SectionSettings = (props) => {
  const columnsContainerDesktopRef = useRef(null);
  const columnsContainerMobileRef = useRef(null);

  const [contextData, builder] = useContext(VariantContextData);
  const preSelectedTab = builder.selected().object_tab;

  const { Sections } = useSections();
  const { SectionColumns } = useSectionColumns();

  const codeIndicators = useCodeIndicators();

  let selected = builder.selected();
  let cta = contextData.objects.cta;
  let variant = contextData.objects.variant;
  let section = builder.selected().object;
  const current_user = contextData.objects.current_user;
  const subscription = contextData.objects.subscription;
  const isBetaOrAdmin =
    current_user &&
    (current_user.admin_type || current_user.beta == true);
  const showFlexUpgradePrompt =
    subscription?.plan_type !== "free" || isBetaOrAdmin;
  let row_element = Object.values(variant.elements).filter(
    (element) => section?.element_id == element.id
  )[0];

  const sectionIndicatorsCustomClass = codeIndicators.get(
    "sections",
    section.id,
    { type: "custom_class", without_column: true }
  );
  const columnIndicatorsCustomClass = (column) =>
    codeIndicators.get("sections", section.id, {
      type: "custom_class",
      column,
    });

  // Changing columns-per-row resets columns to an equal division of the row
  // (clears any per-column width overrides) so the count drives the layout;
  // users can then pick a non-equal preset.
  // The range fires its own builder.update for columns-per-row just before this
  // callback, and both reads share the same (stale) context snapshot, so the
  // last write wins — we re-assert the new columns-per-row value here so it
  // isn't clobbered by the width-clear update.
  const equalizeFlexColumns = (widthKey, perRowKey) => (value) => {
    const updates = flexWidthResetUpdates(
      getLiveFlexSectionColumns(section, variant),
      widthKey,
      () => ""
    );

    updates.push({
      object_type: "sections",
      object_id: section.id,
      setting_name: `[options][structure][${perRowKey}]`,
      value,
    });

    builder.update(updates);
  };

  const label = section.element_id ? "Row" : "Section";
  const isFlex = section.layout_type === "flex";
  const flexColumnCount = getLiveFlexSectionColumns(section, variant).length;
  const tabs = isFlex ? ["Layout", "Design"] : [`${label}`, "Columns"];
  const defaultTab = isFlex ? "Layout" : label;
  const tab = tabs.includes(contextData.selected_object_tab)
    ? contextData.selected_object_tab
    : defaultTab;

  // Legacy sections keep a single content tab (label), so both groups render
  // together. Flex sections split layout vs. design across two tabs. Custom Code
  // is mirrored in both (see the (showLayout || showDesign) gate below).
  const showLayout = isFlex ? tab === "Layout" : tab === label;
  const showDesign = isFlex ? tab === "Design" : tab === label;

  const columnLayoutValue =
    contextData.device == "mobile" &&
    section.options["structure"]["column-layout-mobile"] &&
    section.options["structure"]["mobile-responsive"] == "false"
      ? section.options["structure"]["column-layout-mobile"]
      : section.options["structure"]["column-layout"]
      ? section.options["structure"]["column-layout"]
      : "100";

  useEffect(() => {
    Sections.sortableColumnsStart(section);
  });

  let column_mappings = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
  };

  const [sortedColumns, setSortedColumns] = useState([]);
  const [sortedColumnsMobile, setSortedColumnsMobile] = useState([]);

  const sectionColumnPosition = (column) => {
    return _.get(section, `options.structure.cf-column-${column}-position`);
  };

  useEffect(() => {
    let sorted_columns = Object.keys(column_mappings).sort((a, b) => {
      let a_position = sectionColumnPosition(a);
      let b_position = sectionColumnPosition(b);
      return a_position - b_position;
    });

    let sorted_columns_mobile = Object.keys(column_mappings).sort((a, b) => {
      let a_position = sectionColumnPosition(`${a}-mobile`);
      let b_position = sectionColumnPosition(`${b}-mobile`);
      return a_position - b_position;
    });

    if (isSectionColumnsMobileSorted(section) == false) {
      sorted_columns_mobile = sorted_columns;
    }

    setSortedColumns(sorted_columns);
    setSortedColumnsMobile(sorted_columns_mobile);
  }, [contextData.device]);

  useEffect(() => {
    // Make sure that settings are sorted in the same order as the columns when undoing/redoing builder changes
    const reorderColumns = (divRef) => {
      if (divRef.current) {
        const device = divRef.current.getAttribute("data-device");

        const childrenArray = Array.from(divRef.current.children);

        childrenArray.sort((a, b) => {
          const aSettingName = a
            .getAttribute("data-setting_name")
            .replace(/[\[\]]/g, "");
          const bSettingName = b
            .getAttribute("data-setting_name")
            .replace(/[\[\]]/g, "");

          let aPosition = parseInt(sectionColumnPosition(aSettingName), 10);
          let bPosition = parseInt(sectionColumnPosition(bSettingName), 10);

          if (
            device == "mobile" &&
            isSectionColumnsMobileSorted(section) == false
          ) {
            aPosition = parseInt(
              sectionColumnPosition(aSettingName.replace("-mobile", "")),
              10
            );
            bPosition = parseInt(
              sectionColumnPosition(bSettingName.replace("-mobile", "")),
              10
            );
          }

          return aPosition - bPosition;
        });

        childrenArray.forEach((child) => divRef.current.appendChild(child));
      }
    };

    reorderColumns(columnsContainerDesktopRef);
    reorderColumns(columnsContainerMobileRef);
  }, [contextData.history.undos]);

  const sectionColumnsSettings = () => {
    if (section.layout_type === "flex") return null;
    return (
      <React.Fragment>
        <div
          ref={columnsContainerDesktopRef}
          className="columns-container"
          data-object-type="sections"
          data-object-id={section.id}
          data-device="desktop"
        >
          {sortedColumns.map((column, i) => {
            return (
              <VariantBuilderSetting
                key={`section-${section.id}-column-${i}`}
                object={section}
                setting_name={`[${column}]`}
                setting_type="item_v2"
                position={_.get(
                  section,
                  `options.structure.cf-column-${column}-position`
                )}
                object_type="sections"
                object_id={section.id}
                title={`Column ${column_mappings[column]}`}
                condition={
                  column == "first" ||
                  (column == "second" &&
                    [
                      "50-50",
                      "60-40",
                      "40-60",
                      "70-30",
                      "30-70",
                      "80-20",
                      "20-80",
                      "90-10",
                      "10-90",
                      "33-33-33",
                      "50-25-25",
                      "25-25-50",
                      "25-50-25",
                      "25-25-25-25",
                    ].includes(columnLayoutValue)) ||
                  (column == "third" &&
                    [
                      "33-33-33",
                      "50-25-25",
                      "25-25-50",
                      "25-50-25",
                      "25-25-25-25",
                    ].includes(columnLayoutValue)) ||
                  (column == "fourth" &&
                    ["25-25-25-25"].includes(columnLayoutValue))
                }
                label_max_length={25}
                label_hover_max_length={14}
                style={{
                  marginBottom: "0px",
                }}
                label_icon={"fa-regular fa-square"}
                onEdit={() => {
                  dispatchCustomEvent("selectObject", {
                    object_type: "sections",
                    object_id: section.id,
                    tab: "Columns",
                    collapse: column,
                  });
                }}
                device="desktop"
                hide_duplicate={true}
                hide_remove={true}
              />
            );
          })}
        </div>

        <div
          ref={columnsContainerMobileRef}
          className="columns-container"
          data-object-type="sections"
          data-object-id={section.id}
          data-device="mobile"
        >
          {sortedColumnsMobile.map((column, i) => {
            return (
              <VariantBuilderSetting
                key={`section-${section.id}-column-${i}-mobile`}
                object={section}
                setting_name={`[${column}-mobile]`}
                setting_type="item_v2"
                position={_.get(
                  section,
                  `options.structure.cf-column-${column}-mobile-position`
                )}
                object_type="sections"
                object_id={section.id}
                title={`Column ${column_mappings[column]}`}
                condition={
                  column == "first" ||
                  (column == "second" &&
                    [
                      "50-50",
                      "60-40",
                      "40-60",
                      "70-30",
                      "30-70",
                      "80-20",
                      "20-80",
                      "90-10",
                      "10-90",
                      "33-33-33",
                      "50-25-25",
                      "25-25-50",
                      "25-50-25",
                      "25-25-25-25",
                    ].includes(columnLayoutValue)) ||
                  (column == "third" &&
                    [
                      "33-33-33",
                      "50-25-25",
                      "25-25-50",
                      "25-50-25",
                      "25-25-25-25",
                    ].includes(columnLayoutValue)) ||
                  (column == "fourth" &&
                    ["25-25-25-25"].includes(columnLayoutValue))
                }
                label_max_length={25}
                label_hover_max_length={14}
                style={{
                  marginBottom: "0px",
                }}
                label_icon={
                  _.get(
                    section,
                    `options.structure.${column}-column-hide-mobile`
                  ) == "true"
                    ? "eye-slash"
                    : "fa-regular fa-square"
                }
                onEdit={() => {
                  dispatchCustomEvent("selectObject", {
                    object_type: "sections",
                    object_id: section.id,
                    tab: "Columns",
                    collapse: column,
                  });
                }}
                device="mobile"
                hide_duplicate={true}
                hide_remove={true}
              />
            );
          })}
        </div>
      </React.Fragment>
    );
  };

  const parentElement =
    section.element_id &&
    Object.values(variant.elements).find(
      (element) => element.id == section.element_id
    );
  const parentBackLink = getPanelParentBackLink(
    variant,
    builder.get_object_parent_layer,
    "sections",
    section.id
  );

  return (
    <React.Fragment>
      <div className="row hard-center">
        <VariantBuilderPanelTitle
          title={section.element_id || section.content_toggle_item_id ? "Row" : "Section"}
          object_type="sections"
          object_id={selected.id}
          object={selected.object}
          tooltip={`${
            section.element_id
              ? "Rows are containers of responsive columns that can be nested inside any other column. Choose a layout. Then, style the container and the columns."
              : "Section are containers of responsive columns. Choose a layout. Then, style the container and the columns."
          }`}
          tooltipLabel="Learn"
          parent_object={parentBackLink}
        />

        <TabGroup
          tabs={tabs}
          tab={tab}
          setActiveTab={(tab) => {
            dispatchCustomEvent("selectObject", {
              object_type: "sections",
              object_id: section.id,
              tab: tab,
            });
          }}
          className="mb-6"
        >
          {showLayout && (
            <React.Fragment>
              {parentElement &&
                parentElement.element_type == "conditional_row" && (
                  <VariantBuilderPanelCollapse
                    name="Conditional Row"
                    open={true}
                  >
                    <div className="col-xs-12">
                      <button
                        className="border-[1px] my-5 border-solid py-2 px-5 rounded-lg text-lg mr-1 cursor-pointer bg-slate-950 dark:bg-slate-700 border-slate-950 dark:border-slate-950 text-white"
                        style={{
                          display: "block",
                        }}
                        onClick={() => {
                          const toggle_item = Object.values(
                            contextData.objects.variant.content_toggle_items
                          ).find(
                            (item) => item.id == section.content_toggle_item_id
                          );

                          dispatchCustomEvent("selectObject", {
                            object_type: "elements",
                            object_id: parentElement.id,
                          });

                          setTimeout(() => {
                            dispatchCustomEvent("focusItem", {
                              parent_id: parentElement.id,
                              item_type: "content_toggle_items",
                              item_id: toggle_item.id,
                            });
                          }, 350);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="13"
                          viewBox="0 0 13 13"
                          fill="none"
                          className="mr-2"
                        >
                          <path
                            d="M1.875 11.4578H3.6217C4.30293 11.4578 4.94222 11.1288 5.33817 10.5745L5.93786 9.73493M12.6889 4.04485H10.9422C10.261 4.04485 9.62173 4.37384 9.22578 4.92817L8.55469 5.8677M11.659 2.5L13.125 4.04485L11.659 5.5897M11.659 9.9478L13.125 11.4927L11.659 13.0375M1.875 4.28336H3.6217C4.30293 4.28336 4.94222 4.61235 5.33817 5.16668L9.22578 10.6093C9.62173 11.1637 10.261 11.4927 10.9422 11.4927H12.6889"
                            stroke="white"
                          />
                        </svg>{" "}
                        Edit Conditions
                      </button>
                    </div>
                  </VariantBuilderPanelCollapse>
                )}

              {isFlex ? (
                <React.Fragment>
                  <FlexColumnsTab
                    section={section}
                    variant={variant}
                    SectionColumns={SectionColumns}
                  />

                  <div className="row">
                    <VariantBuilderSetting
                      object={section}
                      setting_name="[options][structure][columns-per-row]"
                      setting_type="range"
                      object_type="sections"
                      object_id={section.id}
                      label="Columns per row"
                      value={section.options["structure"]["columns-per-row"]}
                      min={1}
                      max={flexColumnCount}
                      range_step="1"
                      placeholder="auto"
                      allow_empty={true}
                      device="desktop"
                      show_reset={true}
                      callback={equalizeFlexColumns("width", "columns-per-row")}
                      tooltip="How many columns share a row before they wrap to the next line. Each column defaults to an equal share of the row (e.g. 4 per row = 25% each). Override an individual column's width below."
                      condition={flexColumnCount > 1}
                    />

                    <VariantBuilderSetting
                      object={section}
                      setting_name="[options][structure][columns-per-row-mobile]"
                      setting_type="range"
                      object_type="sections"
                      object_id={section.id}
                      label="Columns per row"
                      value={
                        section.options["structure"]["columns-per-row-mobile"]
                      }
                      min={1}
                      max={flexColumnCount}
                      range_step="1"
                      placeholder="auto"
                      allow_empty={true}
                      device="mobile"
                      show_reset={true}
                      callback={equalizeFlexColumns(
                        "width-mobile",
                        "columns-per-row-mobile"
                      )}
                      tooltip="How many columns share a row on mobile."
                      condition={flexColumnCount > 1}
                    />

                    <VariantBuilderSetting
                      object={section}
                      setting_type="flex_column_layout"
                      object_type="sections"
                      object_id={section.id}
                      label="Column Layout"
                      section={section}
                      variant={variant}
                      device="desktop"
                      show_reset={true}
                      value={
                        flexColumnLayoutCanReset(section, variant, "desktop")
                          ? "1"
                          : ""
                      }
                      onReset={() =>
                        builder.update(
                          flexColumnLayoutResetUpdates(section, variant, "desktop")
                        )
                      }
                    />

                    <VariantBuilderSetting
                      object={section}
                      setting_type="flex_column_layout"
                      object_type="sections"
                      object_id={section.id}
                      label="Column Layout"
                      section={section}
                      variant={variant}
                      device="mobile"
                      show_reset={true}
                      value={
                        flexColumnLayoutCanReset(section, variant, "mobile")
                          ? "1"
                          : ""
                      }
                      onReset={() =>
                        builder.update(
                          flexColumnLayoutResetUpdates(section, variant, "mobile")
                        )
                      }
                    />

                    <SectionLayoutSettings
                      section={section}
                      row_element={row_element}
                    />
                  </div>
                </React.Fragment>
              ) : (
                <VariantBuilderPanelCollapse name="Layout" open={true}>
                  {showFlexUpgradePrompt && (
                    <div className="col-xs-12 mb-4">
                      <div className="row hard-center p-[15px] integration-first-message bg-slate-100 rounded dark:bg-slate-800">
                        <p className="font-bold text-xl text-black dark:text-white">
                          {section.content_toggle_item_id
                            ? "Upgrade to flex layout"
                            : "Duplicate with flex layout"}
                        </p>
                        <p className="text-lg text-black dark:text-white">
                          {section.content_toggle_item_id
                            ? "Upgrade this section to the new flex layout to add an unlimited number of columns."
                            : "Duplicate this section with the new flex layout to add an unlimited number of columns. Your current section stays unchanged so you can cut over when ready."}
                        </p>
                        <button
                          type="button"
                          className="text-lg px-3 py-2 font-bold text-white bg-black dark:bg-black border-transparent rounded-lg"
                          onClick={() => {
                            if (
                              confirm(
                                section.content_toggle_item_id
                                  ? "Upgrade this section to the new flex layout?"
                                  : "Duplicate this section with the new flex layout? Your current section will be left unchanged."
                              )
                            ) {
                              SectionColumns.upgradeSectionToFlex(section);
                            }
                          }}
                        >
                          {section.content_toggle_item_id
                            ? "Upgrade to flex layout"
                            : "Duplicate with flex layout"}
                        </button>
                      </div>
                    </div>
                  )}

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][column-layout]"
                  setting_type="column"
                  object_type="sections"
                  object_id={section.id}
                  label="Column Layout"
                  value={
                    section.options["structure"]["column-layout"]
                      ? section.options["structure"]["column-layout"]
                      : "100"
                  }
                  condition={
                    section.layout_type !== "flex" &&
                    (contextData.device == "desktop" ||
                      section.options["structure"]["mobile-responsive"] !==
                        "false")
                  }
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][column-layout-mobile]"
                  setting_type="column"
                  object_type="sections"
                  object_id={section.id}
                  label="Column Sizes"
                  value={
                    section.options["structure"]["column-layout-mobile"]
                      ? section.options["structure"]["column-layout-mobile"]
                      : section.options["structure"]["column-layout"]
                  }
                  condition={
                    section.layout_type !== "flex" &&
                    contextData.device == "mobile" &&
                    section.options["structure"]["mobile-responsive"] == "false"
                  }
                  device="mobile"
                />

                {section.layout_type !== "flex" && (
                  <div className="col-xs-12 some-vertical-space">
                    <div className="col-xs-12 no-padding-sides">
                      <div className="w-full relative text-black dark:text-white">
                        <span className="text-xl">Columns:</span>

                        {contextData.device == "mobile" && (
                          <span
                            className="glyphicon glyphicon-phone tooltip-icon"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Mobile only"
                            aria-hidden="true"
                            style={{
                              color: "#00b593",
                              fontSize: "14px",
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {sectionColumnsSettings()}
                  </div>
                )}

                <SectionLayoutSettings
                  section={section}
                  row_element={row_element}
                />
                </VariantBuilderPanelCollapse>
              )}
            </React.Fragment>
          )}

          {showDesign && (
            <React.Fragment>
              <VariantBuilderPanelCollapse name="Background" open={false}>
                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-opacity]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={
                    "Background Overlay Opacity " +
                    (section.options["structure"]["background-opacity"]
                      ? section.options["structure"]["background-opacity"]
                      : 1)
                  }
                  value={
                    section.options["structure"]["background-opacity"]
                      ? section.options["structure"]["background-opacity"]
                      : null
                  }
                  placeholder={1}
                  condition={["button", "form"].includes(cta.cta_type) == false}
                  min={0}
                  max={1}
                  range_step={"0.01"}
                  show_reset={true}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-color]"
                  setting_type="color_v2"
                  object_type="sections"
                  object_id={section.id}
                  label="Background Overlay Color"
                  value={
                    section.options["structure"]["background-color"]
                      ? section.options["structure"]["background-color"]
                      : ""
                  }
                  gradient={true}
                  show_reset={true}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-image]"
                  setting_type="image"
                  object_type="sections"
                  object_id={section.id}
                  label="Background Image"
                  value={
                    section.options["structure"]["background-image"] &&
                    section.options["structure"]["background-image"] !== "none"
                      ? section.options["structure"]["background-image"]
                      : ""
                  }
                  show_reset={true}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-position]"
                  setting_type="dropdown"
                  object_type="sections"
                  object_id={section.id}
                  label="Background Image Position"
                  value={
                    section.options["structure"]["background-position"]
                      ? section.options["structure"]["background-position"]
                      : "top-center"
                  }
                  options={[
                    { value: "top-left", text: "Top left" },
                    { value: "top-center", text: "Top center" },
                    { value: "top-right", text: "Top right" },
                    { value: "center-left", text: "Middle left" },
                    { value: "center-center", text: "Middle center" },
                    { value: "center-right", text: "Middle right" },
                    { value: "bottom-left", text: "Bottom left" },
                    { value: "bottom-center", text: "Bottom center" },
                    { value: "bottom-right", text: "Bottom right" },
                    { value: "custom", text: "Custom" },
                  ]}
                  device="desktop"
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-custom-top-position]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Top Custom Position"}
                  placeholder="0px"
                  value={
                    section.options["structure"][
                      "background-custom-top-position"
                    ]
                  }
                  condition={
                    section.options["structure"]["background-position"] ==
                    "custom"
                  }
                  allow_empty={true}
                  show_reset={true}
                  device="desktop"
                  min={-2560}
                  max={2560}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-custom-left-position]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Left Custom Position"}
                  placeholder="0px"
                  value={
                    section.options["structure"][
                      "background-custom-left-position"
                    ]
                  }
                  condition={
                    section.options["structure"]["background-position"] ==
                    "custom"
                  }
                  allow_empty={true}
                  show_reset={true}
                  device="desktop"
                  min={-2560}
                  max={2560}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-position-mobile]"
                  setting_type="dropdown"
                  object_type="sections"
                  object_id={section.id}
                  label="Background Image Position"
                  value={
                    section.options["structure"]["background-position-mobile"]
                      ? section.options["structure"][
                          "background-position-mobile"
                        ]
                      : section.options["structure"]["background-position"]
                      ? section.options["structure"]["background-position"]
                      : "top-center"
                  }
                  options={[
                    { value: "top-left", text: "Top left" },
                    { value: "top-center", text: "Top center" },
                    { value: "top-right", text: "Top right" },
                    { value: "center-left", text: "Middle left" },
                    { value: "center-center", text: "Middle center" },
                    { value: "center-right", text: "Middle right" },
                    { value: "bottom-left", text: "Bottom left" },
                    { value: "bottom-center", text: "Bottom center" },
                    { value: "bottom-right", text: "Bottom right" },
                    { value: "custom", text: "Custom" },
                  ]}
                  device="mobile"
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-custom-top-position-mobile]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Top Custom Position"}
                  placeholder={
                    section.options["structure"][
                      "background-custom-top-position"
                    ] || "0px"
                  }
                  value={
                    section.options["structure"][
                      "background-custom-top-position-mobile"
                    ]
                  }
                  condition={
                    section.options["structure"]["background-position-mobile"]
                      ? section.options["structure"][
                          "background-position-mobile"
                        ] == "custom"
                      : section.options["structure"]["background-position"] ==
                        "custom"
                  }
                  allow_empty={true}
                  show_reset={true}
                  device="mobile"
                  min={-2560}
                  max={2560}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-custom-left-position-mobile]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Left Custom Position"}
                  placeholder={
                    section.options["structure"][
                      "background-custom-left-position"
                    ] || "0px"
                  }
                  value={
                    section.options["structure"][
                      "background-custom-left-position-mobile"
                    ]
                  }
                  condition={
                    section.options["structure"]["background-position-mobile"]
                      ? section.options["structure"][
                          "background-position-mobile"
                        ] == "custom"
                      : section.options["structure"]["background-position"] ==
                        "custom"
                  }
                  allow_empty={true}
                  show_reset={true}
                  device="mobile"
                  min={-2560}
                  max={2560}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-size]"
                  setting_type="dropdown"
                  object_type="sections"
                  object_id={section.id}
                  label="Background Image Size"
                  value={
                    section.options["structure"]["background-size"]
                      ? section.options["structure"]["background-size"]
                      : "cover"
                  }
                  options={[
                    { value: "auto", text: "Image width" },
                    { value: "cover", text: "Cover container" },
                    { value: "expand-width", text: "Expand to width" },
                    { value: "expand-height", text: "Expand to height" },
                    { value: "custom", text: "Custom" },
                  ]}
                  device="desktop"
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-size-mobile]"
                  setting_type="dropdown"
                  object_type="sections"
                  object_id={section.id}
                  label="Background Image Size"
                  value={
                    section.options["structure"]["background-size-mobile"]
                      ? section.options["structure"]["background-size-mobile"]
                      : section.options["structure"]["background-size"]
                      ? section.options["structure"]["background-size"]
                      : "cover"
                  }
                  options={[
                    { value: "auto", text: "Image width" },
                    { value: "cover", text: "Cover container" },
                    { value: "expand-width", text: "Expand to width" },
                    { value: "expand-height", text: "Expand to height" },
                    { value: "custom", text: "Custom" },
                  ]}
                  device="mobile"
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-custom-size]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Custom Image Size"}
                  placeholder="0px"
                  value={section.options["structure"]["background-custom-size"]}
                  condition={
                    section.options["structure"]["background-size"] == "custom"
                  }
                  allow_empty={true}
                  show_reset={true}
                  device="desktop"
                  min={0}
                  max={2560}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-custom-size-mobile]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Custom Image Size"}
                  placeholder={
                    section.options["structure"]["background-custom-size"] ||
                    "0px"
                  }
                  value={
                    section.options["structure"][
                      "background-custom-size-mobile"
                    ]
                  }
                  condition={
                    section.options["structure"]["background-size-mobile"]
                      ? section.options["structure"][
                          "background-size-mobile"
                        ] == "custom"
                      : section.options["structure"]["background-size"] ==
                        "custom"
                  }
                  allow_empty={true}
                  show_reset={true}
                  device="mobile"
                  min={0}
                  max={2560}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][background-video]"
                  setting_type="text"
                  object_type="sections"
                  object_id={section.id}
                  label="Video Background"
                  value={
                    section.options["structure"]["background-video"]
                      ? section.options["structure"]["background-video"]
                      : ""
                  }
                  placeholder="http://"
                  note="YouTube, Vimeo or Wistia URLs. Desktop only."
                  show_reset={true}
                />
              </VariantBuilderPanelCollapse>

              <VariantBuilderPanelCollapse name="Border" open={false}>
                <VariantBuilderSetting
                  object={section}
                  label="Border Style"
                  setting_name="[options][structure][section-border-style]"
                  setting_type="border_style"
                  object_type="sections"
                  object_id={section.id}
                  value={
                    section.options["structure"]["section-border-style"]
                      ? section.options["structure"]["section-border-style"]
                      : "solid"
                  }
                  show_reset={true}
                />

                <VariantBuilderSetting
                  object={section}
                  label="Container Border Color"
                  setting_name={"[options][structure][section-border-color]"}
                  setting_type="color_v2"
                  object_type="sections"
                  object_id={section.id}
                  value={
                    section.options["structure"]["section-border-color"]
                      ? section.options["structure"]["section-border-color"]
                      : "#CCC"
                  }
                  condition={
                    section.options["structure"]["section-border-style"] !==
                    "none"
                  }
                  show_reset={true}
                />

                {section.options["structure"]["section-border-style"] !==
                  "none" && (
                  <div
                    className="col-xs-12 some-vertical-space setting"
                    style={{ marginBottom: "5px" }}
                  >
                    <div className="row">
                      <div className="col-xs-12">
                        <div className="mb-[5px] text-xl text-black dark:text-white">
                          Container Border Width
                        </div>

                        {["left", "top", "bottom", "right"].map((side) => {
                          const settingName = `[options][structure][section-border-width-${side}]`;
                          return (
                            <VariantBuilderSetting
                              key={settingName}
                              object={section}
                              setting_name={settingName}
                              setting_type="number"
                              object_type="sections"
                              object_id={section.id}
                              value={
                                _.isNil(
                                  section.options["structure"][
                                    `section-border-width-${side}`
                                  ]
                                ) == false
                                  ? section.options["structure"][
                                      `section-border-width-${side}`
                                    ]
                                  : "0"
                              }
                              placeholder="0px"
                              col="3"
                              note={window.capitalizeFirstLetter(side)}
                              classNames="spacing-inputs"
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <VariantBuilderSetting
                  object={section}
                  label="Container Corner Rounding"
                  setting_name={"[options][structure][section-border-radius]"}
                  setting_type="border_radius"
                  object_type="sections"
                  object_id={section.id}
                  value={
                    section.options["structure"]["section-border-radius"]
                      ? section.options["structure"]["section-border-radius"]
                      : "0"
                  }
                  valueMappings={{
                    none: "0",
                    some: "4",
                    full: "1000",
                    custom: "custom",
                  }}
                  show_reset={true}
                />

                <div className="col-xs-12">
                  {["top-left", "top-right", "bottom-right", "bottom-left"].map(
                    (side) => {
                      const settingName = `[options][structure][section-border-radius-${side}]`;
                      return (
                        <VariantBuilderSetting
                          key={settingName}
                          object={section}
                          setting_name={settingName}
                          setting_type="number"
                          object_type="sections"
                          object_id={section.id}
                          value={
                            _.isNil(
                              section.options["structure"][
                                `section-border-radius-${side}`
                              ]
                            ) == false
                              ? section.options["structure"][
                                  `section-border-radius-${side}`
                                ]
                              : "0"
                          }
                          condition={
                            section.options["structure"][
                              "section-border-radius"
                            ] == "custom"
                          }
                          min={0}
                          note={capitalizeFirstLetter(side).replace("-", " ")}
                          col="3"
                          placeholder="0px"
                          classNames="spacing-inputs"
                        />
                      );
                    }
                  )}
                </div>

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][hide-overflow-content]"
                  setting_type="checkbox"
                  object_type="sections"
                  object_id={section.id}
                  label="Hide Content Overflow"
                  value={
                    section.options["structure"]["hide-overflow-content"]
                      ? section.options["structure"]["hide-overflow-content"]
                      : "false"
                  }
                  tooltip="Toggling this on will hide any content that overflows the section"
                />
              </VariantBuilderPanelCollapse>

              <VariantBuilderPanelCollapse name="Shadow" open={false}>
                <VariantBuilderSetting
                  object={section}
                  label="Drop Shadow"
                  setting_name="[options][structure][box-shadow]"
                  setting_type="shadow"
                  object_type="sections"
                  object_id={section.id}
                  value={
                    section.options["structure"]["box-shadow"]
                      ? section.options["structure"]["box-shadow"]
                      : "none"
                  }
                  show_reset={true}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][box-shadow-horizontal-distance]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Shadow Horizontal Distance"}
                  value={
                    section.options["structure"][
                      "box-shadow-horizontal-distance"
                    ]
                      ? section.options["structure"][
                          "box-shadow-horizontal-distance"
                        ]
                      : "0"
                  }
                  condition={
                    section.options["structure"]["box-shadow"] == "custom"
                  }
                  min={-100}
                  max={100}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][box-shadow-vertical-distance]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Shadow Vertical Distance"}
                  value={
                    section.options["structure"]["box-shadow-vertical-distance"]
                      ? section.options["structure"][
                          "box-shadow-vertical-distance"
                        ]
                      : "0"
                  }
                  condition={
                    section.options["structure"]["box-shadow"] == "custom"
                  }
                  min={-100}
                  max={100}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][box-shadow-blur-radius]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Shadow Blur Radius"}
                  value={
                    section.options["structure"]["box-shadow-blur-radius"]
                      ? section.options["structure"]["box-shadow-blur-radius"]
                      : "0"
                  }
                  condition={
                    section.options["structure"]["box-shadow"] == "custom"
                  }
                  min={0}
                  max={100}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][box-shadow-spread-radius]"
                  setting_type="range"
                  object_type="sections"
                  object_id={section.id}
                  label={"Shadow Spread Radius"}
                  value={
                    section.options["structure"]["box-shadow-spread-radius"]
                      ? section.options["structure"]["box-shadow-spread-radius"]
                      : "0"
                  }
                  condition={
                    section.options["structure"]["box-shadow"] == "custom"
                  }
                  min={0}
                  max={100}
                  range_step={"1"}
                />

                <VariantBuilderSetting
                  object={section}
                  label="Shadow Color"
                  setting_name="[options][structure][box-shadow-color]"
                  setting_type="color_v2"
                  object_type="sections"
                  object_id={section.id}
                  value={
                    section.options["structure"]["box-shadow-color"]
                      ? section.options["structure"]["box-shadow-color"]
                      : "rgba(0,0,0, 0.35)"
                  }
                  condition={
                    section.options["structure"]["box-shadow"] == "custom"
                  }
                />
              </VariantBuilderPanelCollapse>
            </React.Fragment>
          )}

          {(showLayout || showDesign) && (
            <VariantBuilderPanelCollapse
              name="Custom Code"
              open={false}
              indicators={sectionIndicatorsCustomClass}
            >
                <VariantBuilderSetting
                  object={section}
                  setting_name="[options][structure][custom-class]"
                  label="Custom Classes"
                  indicators={sectionIndicatorsCustomClass}
                  placeholder="Custom class names here"
                  setting_type="text"
                  object_type="sections"
                  object_id={section.id}
                  value={section.options["structure"]["custom-class"]}
                  alphanumeric={true}
                  update_on_input_end={true}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_type="message"
                  label="HTML/Anchor ID"
                  value={"#section" + section.id}
                />

                <VariantBuilderSetting
                  object={section}
                  setting_type="message"
                  label="CSS Selector"
                  value={"#section" + section.id + ".cf-section"}
                  tooltip="Using custom CSS? Make sure to add !important to the end of each of your CSS properties."
                />
            </VariantBuilderPanelCollapse>
          )}
          {tab == "Columns" && section.layout_type !== "flex" && (
            <React.Fragment>
              {["first", "second", "third", "fourth"]
                .filter(
                  (column) =>
                    column == "first" ||
                    (column == "second" &&
                      [
                        "50-50",
                        "60-40",
                        "40-60",
                        "70-30",
                        "30-70",
                        "80-20",
                        "20-80",
                        "90-10",
                        "10-90",
                        "33-33-33",
                        "50-25-25",
                        "25-25-50",
                        "25-50-25",
                        "25-25-25-25",
                      ].includes(columnLayoutValue)) ||
                    (column == "third" &&
                      [
                        "33-33-33",
                        "50-25-25",
                        "25-25-50",
                        "25-50-25",
                        "25-25-25-25",
                      ].includes(columnLayoutValue)) ||
                    (column == "fourth" &&
                      ["25-25-25-25"].includes(columnLayoutValue))
                )
                .map((column, i) => {
                  return (
                    <VariantBuilderPanelCollapse
                      name={`Column ${i + 1}`}
                      indicators={columnIndicatorsCustomClass(column)}
                      open={
                        builder.selected().object_collapse == column
                          ? true
                          : false
                      }
                      key={"tab-section-column-" + column}
                    >
                      <VariantBuilderSetting
                        object={section}
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-SPACING_TYPE-"
                        }
                        setting_type="spacing"
                        object_type="sections"
                        object_id={section.id}
                        label="Column Padding"
                        condition={contextData.device == "desktop"}
                        note="Click to increase and reduce padding"
                        padding_only={true}
                      >
                        <div
                          className="row hard-center"
                          style={{ marginTop: "15px" }}
                        >
                          <div className="col-xs-12">
                            <div className="mb-[5px] text-xl text-black dark:text-white">
                              Inner padding
                            </div>
                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-left]"
                              }
                              setting_type="text"
                              object_type="sections"
                              object_id={section.id}
                              value={builder.helpers.convertLegacyPadding(
                                section.options["structure"][
                                  "cf-column-" + column + "-padding-left"
                                ]
                              )}
                              numeric_with_percentage={true}
                              placeholder="0px"
                              col="3"
                              note="Left"
                              classNames="spacing-inputs"
                              allow_empty={true}
                            />

                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-top]"
                              }
                              setting_type="text"
                              object_type="sections"
                              object_id={section.id}
                              value={builder.helpers.convertLegacyPadding(
                                section.options["structure"][
                                  "cf-column-" + column + "-padding-top"
                                ]
                              )}
                              numeric_with_percentage={true}
                              placeholder="0px"
                              col="3"
                              note="Top"
                              classNames="spacing-inputs"
                              allow_empty={true}
                            />

                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-bottom]"
                              }
                              setting_type="text"
                              object_type="sections"
                              object_id={section.id}
                              value={builder.helpers.convertLegacyPadding(
                                section.options["structure"][
                                  "cf-column-" + column + "-padding-bottom"
                                ]
                              )}
                              numeric_with_percentage={true}
                              placeholder="0px"
                              col="3"
                              note="Bottom"
                              classNames="spacing-inputs"
                              allow_empty={true}
                            />

                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-right]"
                              }
                              setting_type="text"
                              object_type="sections"
                              object_id={section.id}
                              value={builder.helpers.convertLegacyPadding(
                                section.options["structure"][
                                  "cf-column-" + column + "-padding-right"
                                ]
                              )}
                              numeric_with_percentage={true}
                              placeholder="0px"
                              col="3"
                              note="Right"
                              classNames="spacing-inputs"
                              allow_empty={true}
                            />
                          </div>
                        </div>
                      </VariantBuilderSetting>

                      <VariantBuilderSetting
                        object={section}
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-SPACING_TYPE-mobile-"
                        }
                        setting_type="spacing"
                        object_type="sections"
                        object_id={section.id}
                        label="Column Padding"
                        condition={contextData.device == "mobile"}
                        device="mobile"
                        note="Click to increase and reduce padding"
                        padding_only={true}
                      >
                        <div
                          className="row hard-center"
                          style={{ marginTop: "15px" }}
                        >
                          <div className="col-xs-12">
                            <div className="mb-[5px] text-xl text-black dark:text-white">
                              Inner padding
                            </div>
                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-mobile-left]"
                              }
                              setting_type="text"
                              numeric_with_percentage={true}
                              object_type="sections"
                              object_id={section.id}
                              value={
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" +
                                      column +
                                      "-padding-mobile-left"
                                  ]
                                ) ||
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" + column + "-padding-left"
                                  ]
                                )
                              }
                              placeholder="0px"
                              col="3"
                              note="Left"
                              classNames="spacing-inputs"
                            />

                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-mobile-top]"
                              }
                              setting_type="text"
                              numeric_with_percentage={true}
                              object_type="sections"
                              object_id={section.id}
                              value={
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" +
                                      column +
                                      "-padding-mobile-top"
                                  ]
                                ) ||
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" + column + "-padding-top"
                                  ]
                                )
                              }
                              placeholder="0px"
                              col="3"
                              note="Top"
                              classNames="spacing-inputs"
                            />

                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-mobile-bottom]"
                              }
                              setting_type="text"
                              numeric_with_percentage={true}
                              object_type="sections"
                              object_id={section.id}
                              value={
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" +
                                      column +
                                      "-padding-mobile-bottom"
                                  ]
                                ) ||
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" + column + "-padding-bottom"
                                  ]
                                )
                              }
                              placeholder="0px"
                              col="3"
                              note="Bottom"
                              classNames="spacing-inputs"
                            />

                            <VariantBuilderSetting
                              object={section}
                              setting_name={
                                "[options][structure][cf-column-" +
                                column +
                                "-padding-mobile-right]"
                              }
                              setting_type="text"
                              numeric_with_percentage={true}
                              object_type="sections"
                              object_id={section.id}
                              value={
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" +
                                      column +
                                      "-padding-mobile-right"
                                  ]
                                ) ||
                                builder.helpers.convertLegacyPadding(
                                  section.options["structure"][
                                    "cf-column-" + column + "-padding-right"
                                  ]
                                )
                              }
                              placeholder="0px"
                              col="3"
                              note="Right"
                              classNames="spacing-inputs"
                            />
                          </div>
                        </div>
                      </VariantBuilderSetting>

                      <VariantBuilderSetting
                        object={section}
                        setting_name={
                          "[options][structure][" +
                          column +
                          "-column-hide-mobile]"
                        }
                        setting_type="checkbox"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            column + "-column-hide-mobile"
                          ]
                            ? section.options["structure"][
                                column + "-column-hide-mobile"
                              ]
                            : "false"
                        }
                        label="Hide on mobile"
                        condition={
                          section.options["structure"]["mobile-responsive"] !==
                          "false"
                        }
                        tooltip="Want to display this only on desktop? Enable this option"
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Overlay Color"
                        setting_name={
                          "[options][structure][cf-column-" + column + "-color]"
                        }
                        setting_type="color_v2"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "cf-column-" + column + "-color"
                          ]
                            ? section.options["structure"][
                                "cf-column-" + column + "-color"
                              ]
                            : ""
                        }
                        gradient={true}
                        show_reset={true}
                      />

                      <VariantBuilderSetting
                        object={section}
                        label={"Overlay Opacity"}
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-opacity]"
                        }
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "cf-column-" + column + "-opacity"
                          ]
                            ? section.options["structure"][
                                "cf-column-" + column + "-opacity"
                              ]
                            : null
                        }
                        placeholder={0}
                        min={0}
                        max={1}
                        range_step={"0.01"}
                        show_reset={true}
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Overlay Image"
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-background]"
                        }
                        setting_type="image"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "cf-column-" + column + "-background"
                          ]
                            ? section.options["structure"][
                                "cf-column-" + column + "-background"
                              ]
                            : ""
                        }
                        show_reset={true}
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Overlay Image Position"
                        setting_name={
                          "[options][structure][column-" +
                          column +
                          "-background-position]"
                        }
                        setting_type="dropdown"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "column-" + column + "-background-position"
                          ]
                            ? section.options["structure"][
                                "column-" + column + "-background-position"
                              ]
                            : "top-center"
                        }
                        options={[
                          { value: "top-left", text: "Top left" },
                          { value: "top-center", text: "Top center" },
                          { value: "top-right", text: "Top right" },
                          { value: "center-left", text: "Middle left" },
                          { value: "center-center", text: "Middle center" },
                          { value: "center-right", text: "Middle right" },
                          { value: "bottom-left", text: "Bottom left" },
                          { value: "bottom-center", text: "Bottom center" },
                          { value: "bottom-right", text: "Bottom right" },
                          { value: "custom", text: "Custom" },
                        ]}
                        device="desktop"
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_name={`[options][structure][column-${column}-background-custom-top-position]`}
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        label={"Top Custom Position"}
                        placeholder="0px"
                        value={
                          section.options["structure"][
                            `column-${column}-background-custom-top-position`
                          ]
                        }
                        condition={
                          section.options["structure"][
                            `column-${column}-background-position`
                          ] == "custom"
                        }
                        allow_empty={true}
                        show_reset={true}
                        device="desktop"
                        min={-2560}
                        max={2560}
                        range_step={"1"}
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_name={`[options][structure][column-${column}-background-custom-left-position]`}
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        label={"Left Custom Position"}
                        placeholder="0px"
                        value={
                          section.options["structure"][
                            `column-${column}-background-custom-left-position`
                          ]
                        }
                        condition={
                          section.options["structure"][
                            `column-${column}-background-position`
                          ] == "custom"
                        }
                        allow_empty={true}
                        show_reset={true}
                        device="desktop"
                        min={-2560}
                        max={2560}
                        range_step={"1"}
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Overlay Image Position"
                        setting_name={
                          "[options][structure][column-" +
                          column +
                          "-background-position-mobile]"
                        }
                        setting_type="dropdown"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "column-" + column + "-background-position-mobile"
                          ]
                            ? section.options["structure"][
                                "column-" +
                                  column +
                                  "-background-position-mobile"
                              ]
                            : section.options["structure"][
                                "column-" + column + "-background-position"
                              ]
                            ? section.options["structure"][
                                "column-" + column + "-background-position"
                              ]
                            : "top-center"
                        }
                        options={[
                          { value: "top-left", text: "Top left" },
                          { value: "top-center", text: "Top center" },
                          { value: "top-right", text: "Top right" },
                          { value: "center-left", text: "Middle left" },
                          { value: "center-center", text: "Middle center" },
                          { value: "center-right", text: "Middle right" },
                          { value: "bottom-left", text: "Bottom left" },
                          { value: "bottom-center", text: "Bottom center" },
                          { value: "bottom-right", text: "Bottom right" },
                          { value: "custom", text: "Custom" },
                        ]}
                        device="mobile"
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_name={`[options][structure][column-${column}-background-custom-top-position-mobile]`}
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        label={"Top Custom Position"}
                        placeholder="0px"
                        value={
                          section.options["structure"][
                            `column-${column}-background-custom-top-position-mobile`
                          ]
                        }
                        condition={
                          section.options["structure"][
                            `column-${column}-background-position-mobile`
                          ]
                            ? section.options["structure"][
                                `column-${column}-background-position-mobile`
                              ] == "custom"
                            : section.options["structure"][
                                `column-${column}-background-position`
                              ] == "custom"
                        }
                        allow_empty={true}
                        show_reset={true}
                        device="mobile"
                        min={-2560}
                        max={2560}
                        range_step={"1"}
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_name={`[options][structure][column-${column}-background-custom-left-position-mobile]`}
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        label={"Left Custom Position"}
                        placeholder="0px"
                        value={
                          section.options["structure"][
                            `column-${column}-background-custom-left-position-mobile`
                          ]
                        }
                        condition={
                          section.options["structure"][
                            `column-${column}-background-position-mobile`
                          ]
                            ? section.options["structure"][
                                `column-${column}-background-position-mobile`
                              ] == "custom"
                            : section.options["structure"][
                                `column-${column}-background-position`
                              ] == "custom"
                        }
                        allow_empty={true}
                        show_reset={true}
                        device="mobile"
                        min={-2560}
                        max={2560}
                        range_step={"1"}
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Overlay Image Size"
                        setting_name={
                          "[options][structure][column-" +
                          column +
                          "-background-size]"
                        }
                        setting_type="dropdown"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "column-" + column + "-background-size"
                          ]
                            ? section.options["structure"][
                                "column-" + column + "-background-size"
                              ]
                            : "cover"
                        }
                        options={[
                          { value: "auto", text: "Image width" },
                          { value: "cover", text: "Cover container" },
                          { value: "expand-width", text: "Expand to width" },
                          { value: "expand-height", text: "Expand to height" },
                          { value: "custom", text: "Custom" },
                        ]}
                        device="desktop"
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Overlay Image Size"
                        setting_name={
                          "[options][structure][column-" +
                          column +
                          "-background-size-mobile]"
                        }
                        setting_type="dropdown"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "column-" + column + "-background-size-mobile"
                          ]
                            ? section.options["structure"][
                                "column-" + column + "-background-size-mobile"
                              ]
                            : section.options["structure"][
                                "column-" + column + "-background-size"
                              ]
                            ? section.options["structure"][
                                "column-" + column + "-background-size"
                              ]
                            : "cover"
                        }
                        options={[
                          { value: "auto", text: "Image width" },
                          { value: "cover", text: "Cover container" },
                          { value: "expand-width", text: "Expand to width" },
                          { value: "expand-height", text: "Expand to height" },
                          { value: "custom", text: "Custom" },
                        ]}
                        device="mobile"
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_name={`[options][structure][column-${column}-background-custom-size]`}
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        label={"Custom Image Size"}
                        placeholder="0px"
                        value={
                          section.options["structure"][
                            `column-${column}-background-custom-size`
                          ]
                        }
                        condition={
                          section.options["structure"][
                            `column-${column}-background-size`
                          ] == "custom"
                        }
                        allow_empty={true}
                        show_reset={true}
                        device="desktop"
                        min={0}
                        max={2560}
                        range_step={"1"}
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_name={`[options][structure][column-${column}-background-custom-size-mobile]`}
                        setting_type="range"
                        object_type="sections"
                        object_id={section.id}
                        label={"Custom Image Size"}
                        placeholder={
                          section.options["structure"][
                            `column-${column}-background-custom-size`
                          ] || "0px"
                        }
                        value={
                          section.options["structure"][
                            `column-${column}-background-custom-size-mobile`
                          ]
                        }
                        condition={
                          section.options["structure"][
                            `column-${column}-background-size-mobile`
                          ]
                            ? section.options["structure"][
                                `column-${column}-background-size-mobile`
                              ] == "custom"
                            : section.options["structure"][
                                `column-${column}-background-size`
                              ] == "custom"
                        }
                        allow_empty={true}
                        show_reset={true}
                        device="mobile"
                        min={0}
                        max={2560}
                        range_step={"1"}
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Column Border Style"
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-border-style]"
                        }
                        setting_type="border_style"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "cf-column-" + column + "-border-style"
                          ]
                            ? section.options["structure"][
                                "cf-column-" + column + "-border-style"
                              ]
                            : "solid"
                        }
                      />

                      <VariantBuilderSetting
                        object={section}
                        label="Column Border Color"
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-border-color]"
                        }
                        setting_type="color_v2"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "cf-column-" + column + "-border-color"
                          ]
                            ? section.options["structure"][
                                "cf-column-" + column + "-border-color"
                              ]
                            : "#CCC"
                        }
                        condition={
                          section.options["structure"][
                            "cf-column-" + column + "-border-style"
                          ] !== "none"
                        }
                      />

                      {section.options["structure"][
                        "cf-column-" + column + "-border-style"
                      ] !== "none" && (
                        <div
                          className="col-xs-12 some-vertical-space setting"
                          style={{ marginBottom: "5px" }}
                        >
                          <div className="row">
                            <div className="col-xs-12">
                              <div className="mb-[5px] text-xl text-black dark:text-white">
                                Column Border Width
                              </div>

                              {["left", "top", "bottom", "right"].map(
                                (side) => {
                                  const settingName = `[options][structure][cf-column-${column}-border-width-${side}]`;
                                  return (
                                    <VariantBuilderSetting
                                      key={settingName}
                                      object={section}
                                      setting_name={settingName}
                                      setting_type="number"
                                      object_type="sections"
                                      object_id={section.id}
                                      value={
                                        _.isNil(
                                          section.options["structure"][
                                            `cf-column-${column}-border-width-${side}`
                                          ]
                                        ) == false
                                          ? section.options["structure"][
                                              `cf-column-${column}-border-width-${side}`
                                            ]
                                          : section.options["structure"][
                                              `cf-column-${side}-border-width`
                                            ]
                                          ? section.options["structure"][
                                              `cf-column-${side}-border-width`
                                            ].replace("px", "")
                                          : "0"
                                      }
                                      placeholder="0px"
                                      col="3"
                                      note={window.capitalizeFirstLetter(side)}
                                      classNames="spacing-inputs"
                                      min={0}
                                    />
                                  );
                                }
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <VariantBuilderSetting
                        object={section}
                        setting_name={
                          "[options][structure][cf-column-" +
                          column +
                          "-custom-class]"
                        }
                        label="Custom Classes"
                        indicators={columnIndicatorsCustomClass(column)}
                        placeholder="Custom class names here"
                        setting_type="text"
                        object_type="sections"
                        object_id={section.id}
                        value={
                          section.options["structure"][
                            "cf-column-" + column + "-custom-class"
                          ]
                        }
                        alphanumeric={true}
                        update_on_input_end={true}
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_type="message"
                        label="HTML/Anchor ID"
                        value={"#section" + section.id + "-column-" + column}
                      />

                      <VariantBuilderSetting
                        object={section}
                        setting_type="message"
                        label="CSS Selector"
                        value={
                          "#section" +
                          section.id +
                          "-column-first.cf-column-" +
                          column
                        }
                        tooltip="Using custom CSS? Make sure to add !important to the end of each of your CSS properties."
                      />
                    </VariantBuilderPanelCollapse>
                  );
                })}
            </React.Fragment>
          )}
        </TabGroup>
      </div>
    </React.Fragment>
  );
};

const FlexColumnsTab = ({ section, variant, SectionColumns }) => {
  const containerRef = React.useRef(null);
  const sortableInstanceRef = React.useRef(null);

  const section_columns = getLiveFlexSectionColumns(section, variant);

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    sortableInstanceRef.current = SectionColumns.sortableStart(
      section,
      containerRef.current
    );
    return () => {
      try {
        sortableInstanceRef.current?.destroy();
      } finally {
        sortableInstanceRef.current = null;
      }
    };
  }, [section.id]);

  return (
    <React.Fragment>
      <div className="col-xs-12" style={{ padding: "0" }}>
        <span className="text-xl text-black dark:text-white">Columns</span>
        <div
          ref={containerRef}
          className="col-xs-12 sortable-container mt-4"
          data-sortable-name="section-columns"
          data-object_type="sections"
          data-object_id={section.id}
          style={{ padding: "0" }}
        >
          {section_columns.map((section_column) => (
            <React.Fragment key={`section-column-setting-${section_column.id}`}>
              <SectionColumnSettings section_column={section_column} />
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <NewSectionColumn section={section} />
      </div>
    </React.Fragment>
  );
};

export default SectionSettings;
